import { useDetailsDrawer } from '@src/components/DetailsDrawer/useDetailsDrawer';
import { orderSliceActions } from '@src/components/OMS/NewOrder/OrderSlice';
import { rfqSliceActions } from '@src/components/OMS/NewRFQ/RFQSlice';
import { careOrderToPrimeOrderParams, careOrderToPrimeRFQParams } from '@src/components/OMS/NewRFQ/tradeCareOrder';
import { openView } from '@src/components/OMS/OMSSlice';
import { OMSView } from '@src/components/OMS/OMSView';
import { useFeatureFlag } from '@src/hooks';
import { useTradingSettings } from '@src/providers/AppConfigProvider';
import { useAppStateDispatch } from '@src/providers/AppStateProvider';
import type { BlotterTableRow, CareOrder, Quote } from '@talos/kyoko';
import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  CollapseAllButton,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  Divider,
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  ExpandAllButton,
  ExportMenuItems,
  FormControlSizes,
  IconName,
  MixpanelEvent,
  Toggle,
  useAccordionFilterBuilder,
  useBlotterTable,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useDateRangeFilter,
  useDisclosureWithContext,
  useMixpanel,
  usePersistedBlotterTable,
  type BlotterTableFilter,
  type Column,
} from '@talos/kyoko';
import type { GridOptions } from 'ag-grid-community';
import type { RowDoubleClickedEvent } from 'ag-grid-enterprise';
import { useCallback, useMemo, useState } from 'react';
import type { Observable } from 'rxjs';
import styled from 'styled-components';
import { CareOrderBlotterHUD } from './CareOrderBlotterHUD';
import { CareOrderNetButton } from './CareOrderNetButton';
import { CareOrderManualFillDialog, type CareOrderManualFillDialogContext } from './Dialogs/CareOrderManualFillDialog';
import { IncludeOpenCareOrdersOutsideDateRangeToggle } from './IncludeOpenCareOrdersOutsideDateRangeToggle';
import { useNetCareOrdersMenuItem } from './MenuItems/useNetCareOrdersMenuItem';
import { ValidateFixingDialog } from './ValidateFixing/ValidateFixingDialog';
import { getCareOrderExportOptions } from './careOrderExportOptions';
import { useCareOrderBlotterAutoGroupColDef } from './columns/useCareOrderBlotterAutoGroupColDef';
import { useCareOrderBlotterDataObs } from './pipeline/useCareOrderBlotterDataObs';
import { isCareOrderRow, isQuoteRow, type CareOrderBlotterEntity } from './types';
import { useCareOrderColumns } from './useCareOrderColumns';
import {
  careOrderTimestampFields,
  useCareOrderFilter,
  type CareOrderFilter,
  type CareOrderTimestampField,
} from './useCareOrderFilter';
import { getSelectedCareOrderRowsFromNodes, useCareOrderMenu } from './useCareOrderMenu';

function getDataPath(row: CareOrderBlotterEntity): string[] {
  return row.dataPath;
}

interface CareOrdersTabData {
  showAllDecimals?: boolean;
  expandAllGroups?: boolean;
  includeOpenCareOrdersOutsideDateRange?: boolean;
}

export interface CareOrderBlotterParams {
  blotterID: string;
  portalId?: string;
  defaultFilter?: BlotterTableFilter;
  defaultColumns?: (keyof CareOrder | Partial<Column>)[];
  showHUD?: boolean;
  tabData?: CareOrdersTabData;
  onUpdateTabData?: (data: CareOrdersTabData) => void;
  onCloneTab?: () => void;
}

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  // Always show the left-pinned line
  && .ag-pinned-left-cols-container,
  && .ag-pinned-left-header {
    border-right: 1px solid var(--ag-border-color);
  }
`;

export function CareOrderBlotter({
  blotterID,
  portalId,
  defaultColumns,
  defaultFilter,
  showHUD,
  tabData,
  onUpdateTabData,
  onCloneTab,
}: CareOrderBlotterParams) {
  const mixpanel = useMixpanel();
  const dispatch = useAppStateDispatch();
  const { showAllDecimals, includeOpenCareOrdersOutsideDateRange = true } = tabData ?? EMPTY_OBJECT;
  const { showAllDecimalsInBlotters } = useTradingSettings();
  const {
    enableCareOrderNetting,
    enableFlatCareOrderBlotterExport,
    enableTakerValidateFixing,
    careOrderExportMakerList,
  } = useFeatureFlag();

  const onSendRFQ = useCallback(
    (entity: CareOrder) => {
      dispatch(openView(OMSView.RFQForm));
      dispatch(rfqSliceActions.primeNewRFQForm(careOrderToPrimeRFQParams(entity)));
    },
    [dispatch]
  );

  const onShowRFQ = useCallback(
    (entity: Quote) => {
      dispatch(openView(OMSView.RFQForm));
      dispatch(rfqSliceActions.showRFQ(entity));
    },
    [dispatch]
  );

  const onSendOrder = useCallback(
    (entity: CareOrder) => {
      dispatch(openView(OMSView.NewOrderForm));
      dispatch(orderSliceActions.primeNewOrderForm(careOrderToPrimeOrderParams(entity)));
    },
    [dispatch]
  );

  const manualFillDisclosure = useDisclosureWithContext<CareOrderManualFillDialogContext>();
  const onManualFillCareOrders = useCallbackRef((selectedCareOrders: CareOrder[]) => {
    manualFillDisclosure.open({ selectedCareOrders });
  });

  const { openDetailsDrawer } = useDetailsDrawer();
  const onShowTimeline = useCallbackRef((entity: CareOrderBlotterEntity) => {
    if (isQuoteRow(entity) || isCareOrderRow(entity)) {
      openDetailsDrawer({ entity });
    }
  });

  const defaultCareOrderColumns = useCareOrderColumns({
    defaultColumns,
    onSendRFQ,
    onManualFillCareOrders,
    onShowRFQ,
    onShowTimeline,
  });
  const persistedBlotterTable = usePersistedBlotterTable(blotterID, { filter: defaultFilter });
  const enableCareOrderExportMakerColumns = careOrderExportMakerList.length > 0;

  const handleDoubleClickRow = useCallbackRef(({ data }: RowDoubleClickedEvent<CareOrderBlotterEntity>) => {
    if (isQuoteRow(data) || isCareOrderRow(data)) {
      openDetailsDrawer({ entity: data });
    }
  });

  const careOrderFilter = useCareOrderFilter({
    initialFilter: persistedBlotterTable.initialState?.filter,
    saveFilter: persistedBlotterTable.onFilterChanged,
    includeOpenCareOrdersOutsideDateRange,
  });

  const dateRangeFilter = useDateRangeFilter<CareOrderTimestampField, CareOrderFilter>(
    careOrderFilter.filter,
    careOrderFilter.changeFilter
  );
  const { dataObservable, fullDataObservable, errorObservable } = useCareOrderBlotterDataObs({
    enableCareOrderNetting,
    careOrderBranchFilter: careOrderFilter.careOrderBranchFilter,
  });
  const [selectedCareOrders, setSelectedCareOrders] = useState<CareOrder[]>([]);

  const netCareOrdersMenu = useNetCareOrdersMenuItem<CareOrder>();
  const { filterBuilderProps } = careOrderFilter;
  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: false },
    filterBuilderProps,
  });

  const careOrderMenu = useCareOrderMenu({
    filterableProperties: careOrderFilter.filterableProperties,
    openClause: filterBuilderAccordion.openClause,
    onSendRFQ,
    onSendOrder,
    onManualFillCareOrders,
    netCareOrdersMenu,
  });

  const autoGroupColumnDef = useCareOrderBlotterAutoGroupColDef();
  const gridOptions: GridOptions<CareOrderBlotterEntity> = useMemo(
    () => ({
      onRowDoubleClicked: handleDoubleClickRow,
      autoGroupColumnDef,
      groupDefaultExpanded: tabData?.expandAllGroups ? -1 : undefined,
      treeData: true,
      getDataPath,
      tooltipInteraction: true,
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      onSelectionChanged: event => {
        setSelectedCareOrders(getSelectedCareOrderRowsFromNodes(event.api.getSelectedNodes()));
      },
      processUnpinnedColumns: () => EMPTY_ARRAY,
    }),
    [handleDoubleClickRow, autoGroupColumnDef, tabData?.expandAllGroups]
  );

  const exportOptions = useMemo(() => {
    return getCareOrderExportOptions({
      enableFlatCareOrderBlotterExport,
      enableCareOrderExportMakerColumns,
    });
  }, [enableFlatCareOrderBlotterExport, enableCareOrderExportMakerColumns]);

  const blotterTableSettings = useMemo(
    () => ({
      showAllDecimals: showAllDecimals ?? false,
    }),
    [showAllDecimals]
  );

  const blotterTable = useBlotterTable<CareOrderBlotterEntity>({
    dataObservable,
    errorObservable,
    rowID: 'rowID',
    persistence: persistedBlotterTable,
    columns: defaultCareOrderColumns,
    gridOptions,
    contextMenuOptions: {
      getCustomContextMenuItems: careOrderMenu.getCustomContextMenuItems,
    },
    exportOptions,
    settings: blotterTableSettings,
  });

  const { expandAllGroups, collapseAllGroups } = blotterTable;

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const handleExpandAllGroups = useCallback(() => {
    expandAllGroups();
    onUpdateTabData?.({ expandAllGroups: true });
  }, [expandAllGroups, onUpdateTabData]);

  const handleCollapseAllGroups = useCallback(() => {
    collapseAllGroups();
    onUpdateTabData?.({ expandAllGroups: false });
  }, [collapseAllGroups, onUpdateTabData]);

  const validateFixingDialog = useDisclosureWithContext<Observable<Map<string, CareOrderBlotterEntity>>>();

  const handleShowAllDecimalsChange = useCallback(
    (value: boolean) => {
      onUpdateTabData?.({ showAllDecimals: value });
    },
    [onUpdateTabData]
  );
  const handleIncludeOpenCareOrdersOutsideDateRangeChange = useCallback(
    (value: boolean) => {
      onUpdateTabData?.({ includeOpenCareOrdersOutsideDateRange: value });
    },
    [onUpdateTabData]
  );
  const handleCloneTab = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    onCloneTab?.();
    extrasMenuPopover.close();
  });

  return (
    <Wrapper>
      <BlotterTableFilters
        portalId={portalId}
        {...filterBuilderAccordion}
        {...blotterTable.blotterTableFiltersProps}
        {...dateRangeFilter}
        timestampFields={careOrderTimestampFields}
        quickFilterPrefix={
          enableCareOrderNetting ? (
            <CareOrderNetButton selectedCareOrders={selectedCareOrders} netCareOrdersMenu={netCareOrdersMenu} />
          ) : undefined
        }
        suffix={
          <>
            <ExpandAllButton onClick={handleExpandAllGroups} data-testid="expand-all-button" />
            <CollapseAllButton onClick={handleCollapseAllGroups} data-testid="collapse-all-button" />
            <Divider orientation="vertical" mx="spacing4" />
            <BlotterTableExtrasMenu {...extrasMenuPopover}>
              {!showAllDecimalsInBlotters && onUpdateTabData && (
                <Toggle
                  size={FormControlSizes.Small}
                  checked={showAllDecimals ?? false}
                  onChange={handleShowAllDecimalsChange}
                  label="Show All Decimals"
                />
              )}
              {onUpdateTabData && (
                <IncludeOpenCareOrdersOutsideDateRangeToggle
                  checked={includeOpenCareOrdersOutsideDateRange}
                  onChange={handleIncludeOpenCareOrdersOutsideDateRangeChange}
                />
              )}
              {enableTakerValidateFixing && (
                <Button
                  size={FormControlSizes.Small}
                  variant={ButtonVariants.Default}
                  startIcon={IconName.ShieldCheck}
                  onClick={() => validateFixingDialog.open(fullDataObservable)}
                  data-testid="validate-fixing-button"
                >
                  Validate Fixing
                </Button>
              )}
              {onCloneTab && (
                <Button
                  startIcon={IconName.Duplicate}
                  variant={ButtonVariants.Default}
                  size={FormControlSizes.Small}
                  onClick={handleCloneTab}
                >
                  Clone Tab
                </Button>
              )}
              <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
            </BlotterTableExtrasMenu>
          </>
        }
      />
      {showHUD && <CareOrderBlotterHUD blotterTable={blotterTable} getCareOrder={getCareOrderFromBlotterTableRow} />}
      <BlotterTable {...blotterTable} />
      {careOrderMenu.dialogs}
      <CareOrderManualFillDialog disclosure={manualFillDisclosure} />
      <ValidateFixingDialog {...validateFixingDialog} />
    </Wrapper>
  );
}

function getCareOrderFromBlotterTableRow(row: BlotterTableRow<CareOrderBlotterEntity>) {
  return row.data != null && isCareOrderRow(row.data) ? row.data : undefined;
}
